/**
 * tier.ts — build-time free/premium gating.
 *
 * The tier is baked at BUILD TIME via Vite mode files: `npm run build:free`
 * runs `vite build --mode free`, which loads `.env.free` and sets
 * VITE_APP_TIER=free. The default build (no mode) is the full premium map.
 * Because the flag is a compile-time constant, Rollup dead-code-eliminates
 * the premium-only branches from the free bundle — the free build never even
 * requests future COG frames (see fetchKeyframes in app.tsx).
 *
 * WHAT'S FREE (the "am I safe / what's happening now" tier):
 *   • Radar — past loop only, no nowcast/future frames
 *   • Severe Warnings + All Weather Alerts (safety data is never paywalled)
 *   • Satellite: Infrared only
 *   • Temperature (raster only — METAR observation pills are premium)
 *   • Wind (raster only — animated streamlines are premium)
 *   • Tropical Systems (hurricane cones/tracks — safety-relevant, stays free)
 *   • Storm Outlook / SPC, all days
 *
 * WHAT'S PREMIUM ("forecasting and analysis"):
 *   • The FUTURE half of the timeline on every layer (radar nowcast + GFS
 *     forecast frames)
 *   • Lightning overlay
 *   • Satellite: Visible + Water Vapor
 *   • The whole Sky section (Cloud Cover, Visibility)
 *   • Apparent Temp, Relative Humidity, Dewpoint
 *   • METAR station observation pills
 *   • Wind streamline animation
 *
 * SECURITY NOTE — this is a CLIENT-side gate only. It's honest UX gating, not
 * enforcement: a motivated user could hit the COG endpoints directly. Real
 * enforcement belongs server-side in /api/cog-token: issue tier-scoped tokens
 * and have the Worker refuse to sign/serve future-frame keys (and premium
 * prefixes like r2_tcc_vis) for free tokens. The client changes here are
 * designed so that when that lands, nothing in the UI needs to move.
 */

export type AppTier = "free" | "premium";

export const APP_TIER: AppTier =
  (import.meta as any).env?.VITE_APP_TIER === "free" ? "free" : "premium";

export const IS_FREE = APP_TIER === "free";

/** Where the upsell toast's "Upgrade" link points. */
export const PREMIUM_URL = "https://stormchance.io/premium";

// ── Premium feature registry ────────────────────────────────────────────────
// Layer keys (WEATHER_LAYERS) that are premium-only.
export const PREMIUM_LAYERS = new Set<string>([
  "satellite_shortwave",   // Visible satellite
  "satellite_water_vapor", // Water vapor satellite
  "cloud_cover",           // (also covered by the Sky section lock)
  "visibility",
  "apparent_temperature",
  "relative_humidity",
  "dewpoint",
]);

// Overlay keys (StormChanceUI `overlays` state) that are premium-only.
export const PREMIUM_OVERLAYS = new Set<string>([
  "lightning",
]);

// Whole MENU sections that are premium-only (every sub-item locked and the
// section icon itself locked in the collapsed rail / mobile pill).
export const PREMIUM_SECTIONS = new Set<string>([
  "sky",
]);

export const isLayerLocked = (key: string | null | undefined): boolean =>
  IS_FREE && !!key && PREMIUM_LAYERS.has(key);

export const isOverlayLocked = (key: string | null | undefined): boolean =>
  IS_FREE && !!key && PREMIUM_OVERLAYS.has(key);

export const isSectionLocked = (id: string): boolean =>
  IS_FREE && PREMIUM_SECTIONS.has(id);

// ── Upsell event bus ────────────────────────────────────────────────────────
// Tiny module-level pub/sub so ANY component (menu items in StormChanceUI, the
// timeline's FUTURE chip, the layer-selection guard in app.tsx) can trigger
// the single upsell toast rendered at the app root, without prop-drilling a
// callback through every render path.
type UpsellListener = (featureLabel: string) => void;
const upsellListeners = new Set<UpsellListener>();

/** Show the upsell toast for a premium feature the user just tapped. */
export function requestUpsell(featureLabel: string): void {
  for (const l of upsellListeners) l(featureLabel);
}

/** Subscribe to upsell requests. Returns an unsubscribe function. */
export function onUpsell(listener: UpsellListener): () => void {
  upsellListeners.add(listener);
  return () => upsellListeners.delete(listener);
}
